// Mirrors docs/message-components.md exactly — `components_json`/`modal_json` are opaque JSON
// strings on the backend (no proto `oneof` per visual kind, see that doc's "Where this is
// enforced" section), so these types describe the raw parsed JSON verbatim, field names included
// (camelCase, as the JSON actually carries them — not converted to snake_case like the rest of
// this app's Rust-sourced types).

export type ButtonStyle = 'primary' | 'secondary' | 'success' | 'danger' | 'link'

export interface ButtonComponent {
  type: 'button'
  style?: ButtonStyle
  customId?: string
  url?: string
  label?: string
  emoji?: string
  disabled?: boolean
}

export interface SelectOption {
  label: string
  value: string
}

export interface SelectComponent {
  type: 'select' | 'combobox'
  customId: string
  placeholder?: string
  options: SelectOption[]
  minValues?: number
  maxValues?: number
  /** `combobox` only — tells the client to render a filter/type-ahead box above the option list. */
  searchable?: boolean
}

export interface CheckboxComponent {
  type: 'checkbox'
  customId: string
  label: string
  checked?: boolean
}

export type MessageComponent = ButtonComponent | SelectComponent | CheckboxComponent

export interface ActionRow {
  type: 'actionRow'
  components: MessageComponent[]
}

export type TextInputStyle = 'short' | 'paragraph'

export interface TextInputComponent {
  type: 'textInput'
  customId: string
  label: string
  style?: TextInputStyle
  required?: boolean
  placeholder?: string
  maxLength?: number
}

export type ModalComponent = TextInputComponent | CheckboxComponent

export interface ModalActionRow {
  type: 'actionRow'
  components: ModalComponent[]
}

export interface ParsedModal {
  title: string
  customId: string
  components: ModalActionRow[]
}

/** Parses `components_json` off a message; returns `[]` (never throws) on missing/malformed input. */
export function parseComponentsJson(componentsJson: string | null | undefined): ActionRow[] {
  if (!componentsJson) return []
  try {
    const parsed = JSON.parse(componentsJson)
    return Array.isArray(parsed) ? (parsed as ActionRow[]) : []
  } catch {
    return []
  }
}

/** Parses an `InteractionResult.modal_json`; returns `null` (never throws) on malformed input. */
export function parseModalJson(modalJson: string | null | undefined): ParsedModal | null {
  if (!modalJson) return null
  try {
    const parsed = JSON.parse(modalJson) as Record<string, unknown>
    const components = Array.isArray(parsed.components) ? (parsed.components as ModalActionRow[]) : []
    return {
      title: typeof parsed.title === 'string' ? parsed.title : '',
      customId: typeof parsed.customId === 'string' ? parsed.customId : '',
      components,
    }
  } catch {
    return null
  }
}

/** All field values a modal submission collects, keyed by `customId` — see `SubmitModalRequest.fields`. */
export function initialModalValues(modal: ParsedModal): Record<string, string> {
  const values: Record<string, string> = {}
  for (const row of modal.components) {
    for (const component of row.components) {
      if (component.type === 'textInput') values[component.customId] = ''
      else if (component.type === 'checkbox') values[component.customId] = component.checked ? 'true' : 'false'
    }
  }
  return values
}
